import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  Select,
  TextArea,
  FormField,
  Toggle,
  FileUpload,
  Alert,
  Tooltip,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { TRACKING_OPTIONS } from '@/types/product';
import type { TrackingOverride } from '@/types/product';
import { locationService } from '@/services/location.service';
import { CategoryCombobox } from '@/components/CategoryCombobox';
import { variantAttributeService, VARIANT_ATTRIBUTES_QUERY_KEY } from '@/services/variant-attribute.service';
import { VariantMatrixBuilder } from '@/components/products/VariantMatrixBuilder';
import { useAuthStore } from '@/stores/use-auth-store';
import { authService } from '@/services/auth.service';
import { buildTaxSlabOptions, UNIT_OPTIONS } from '@/constants/product';
import { GLASS, GLASS_FORM, cn, toneClasses } from '@/constants/design-tokens';
import { formatPaise, marginPercent } from '@/utils/currency';
import { generateSku } from '@/utils/sku';
import { rupeeInputToPaise, paiseToRupeeInput } from '@/utils/price-input';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { getStandardProductTaxRate, REGIME_PRODUCT_TAX_RATES } from '@bbpos/types';
import type { CreateProductPayload, GstRate } from '@/types/product';
import { uploadProductImage } from './product-image-upload';
import { productImageService } from '@/services/product-image.service';
import { MAX_RAW_UPLOAD_BYTES } from '@/lib/upload-file';
import { BarcodeScanField } from '@/components/BarcodeScanField';
import { apiErrorField, apiErrorMessage } from '@/lib/api-error-message';
import {
  type VariantRow,
  type AttributeDef,
  findDuplicateCombinations,
  variantsMissingPrice,
  findDuplicateIdentifiers,
  buildCreateVariantsPayload,
} from './product-variant-payload';
import { validateDraft, applyDraftDefaults } from './products/draft-product';
import { toProductUpdate } from './products/product-update-payload';

// GOODS is inventory-tracked; SERVICE is not (labour, delivery, repair).
/** The dropzone's promise: the first is the main image, the rest the gallery. */
const MAX_CREATE_IMAGES = 5;

const PRODUCT_TYPE_OPTIONS = [
  { value: 'GOODS', label: 'Goods' },
  { value: 'SERVICE', label: 'Service' },
];

// ─── Step config per vertical ────────────────────────────────────────────────

interface StepDef {
  id: string;
  label: string;
  icon: string;
}

const BASE_STEPS: StepDef[] = [
  { id: 'basic', label: 'Basic Info', icon: 'M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z' },
  { id: 'pricing', label: 'Pricing & Tax', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'stock', label: 'Stock', icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4' },
  { id: 'images', label: 'Images', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
];

const RETAIL_EXTRA: StepDef = { id: 'variants', label: 'Variants', icon: 'M4 6h16M4 10h16M4 14h16M4 18h16' };
const RESTAURANT_EXTRA: StepDef = { id: 'recipe', label: 'Recipe', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' };

// ─── Step Icon component ────────────────────────────────────────────────────

function StepIcon({ d }: { d: string }) {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

// ─── Location stock entry ────────────────────────────────────────────────────

interface LocationStockEntry {
  location_id: string;
  location_name: string;
  quantity: string;
}

// ─── Main component ─────────────────────────────────────────────────────────

function ProductCreateContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Set once a draft has been saved (or when resuming one via ?draft=<id>), so
  // later saves update that row instead of creating another product.
  const resumeDraftId = searchParams.get('draft');
  const [draftId, setDraftId] = useState<string | null>(resumeDraftId);
  const currency = useTenantCurrency();
  const tax = useTaxRegime();
  const profile = useAuthStore((s) => s.profile);
  const businessType = profile?.tenant?.business_type ?? 'RETAIL';
  // The rate a fresh form starts on when the tenant set no default slab: the
  // regime's standard rate (Oman 5 % VAT). India has no single standard rate
  // (per-slab sentinel); 18 % is its legacy default.
  const regimeDefaultRate = String(
    tax.regime === 'GST_INDIA' ? 18 : getStandardProductTaxRate(currency),
  );
  const isRestaurant = businessType === 'RESTAURANT';

  const steps = useMemo(() => {
    const extra = businessType === 'RESTAURANT' ? RESTAURANT_EXTRA : RETAIL_EXTRA;
    return [...BASE_STEPS, extra];
  }, [businessType]);

  const [currentStep, setCurrentStep] = useState('basic');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form state
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [unit, setUnit] = useState('pcs');
  // Price/cost hold the canonical paise value; the *Rupees states drive the
  // inputs so users type rupees (e.g. 399) instead of paise (39900).
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [priceRupees, setPriceRupees] = useState('');
  // POS comments item 3: a product with variants is priced per variant, so the
  // single price/cost fields give way to the Variants step's per-row prices.
  const [hasVariants, setHasVariants] = useState(false);
  const [costRupees, setCostRupees] = useState('');
  const [gstRate, setGstRate] = useState<string>(regimeDefaultRate);
  // Purchase (input) GST on the cost price. Defaults to match the sales GST —
  // the common case (same HSN) — but is independently editable.
  const [purchaseGstRate, setPurchaseGstRate] = useState<string>(regimeDefaultRate);
  // Per-product (item-level) commission %, retail only.
  const [commissionPct, setCommissionPct] = useState<string>('');
  const [tracking, setTracking] = useState<TrackingOverride>('DEFAULT');
  const [hsnCode, setHsnCode] = useState('');
  const [brand, setBrand] = useState('');
  const [productType, setProductType] = useState('GOODS');
  const [lowStockThreshold, setLowStockThreshold] = useState('10');
  const [locationStocks, setLocationStocks] = useState<LocationStockEntry[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  // Images 2-5 from the dropzone: added to the product's gallery once it exists.
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);

  // Resuming a draft: reload what was saved so the user picks up where they
  // left off rather than retyping the earlier steps.
  const { data: draftProduct, isLoading: draftLoading } = useQuery({
    queryKey: ['product', resumeDraftId],
    queryFn: () => productService.getById(resumeDraftId!),
    enabled: !!resumeDraftId,
    staleTime: 0,
  });

  const draftHydrated = useRef(false);
  useEffect(() => {
    if (!draftProduct || draftHydrated.current) return;
    draftHydrated.current = true;
    setName(draftProduct.name ?? '');
    setSku(draftProduct.sku ?? '');
    setBarcode(draftProduct.barcode ?? '');
    setCategory(draftProduct.category ?? '');
    setUnit(draftProduct.unit ?? 'pcs');
    setPrice(draftProduct.price != null ? String(draftProduct.price) : '');
    setCost(draftProduct.cost != null ? String(draftProduct.cost) : '');
    setPriceRupees(paiseToRupeeInput(draftProduct.price, currency));
    setCostRupees(paiseToRupeeInput(draftProduct.cost, currency));
    setGstRate(String(draftProduct.gst_rate ?? 18));
    setPurchaseGstRate(String((draftProduct as { purchase_gst_rate?: number }).purchase_gst_rate ?? draftProduct.gst_rate ?? 18));
    setTracking(((draftProduct as { tracking?: TrackingOverride }).tracking) ?? 'DEFAULT');
    setHsnCode(draftProduct.hsn_code ?? '');
    setBrand(draftProduct.brand ?? '');
    setProductType(draftProduct.product_type ?? 'GOODS');
    // null = the draft was saved with Min Stock left empty (C04).
    setLowStockThreshold(draftProduct.low_stock_threshold === null ? '' : String(draftProduct.low_stock_threshold ?? 10));
    setImageUrl(draftProduct.image_url ?? '');
    setImagePreview(draftProduct.image_url ?? '');
    // A resumed draft is still a draft; `isActive` becomes meaningful only when
    // the user completes it, so start it in the sellable position.
    setIsActive(true);
  }, [draftProduct, currency]);

  // Revoke the previous object URL when the preview changes, and the current
  // one on unmount.
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);
  useEffect(() => {
    return () => galleryPreviews.forEach((u) => URL.revokeObjectURL(u));
  }, [galleryPreviews]);
  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [attributes, setAttributes] = useState<AttributeDef[]>([]);

  // Tax-inclusive preview for the margin panel. `price`/`cost` are stored
  // tax-EXCLUSIVE (billing adds tax on top — see billing.repository
  // `line_total = subtotal + tax`), so the margin itself stays tax-free: the
  // tax is collected on the sale and reclaimed on the purchase, so it is never
  // the shop's money. These figures are the actual cash that changes hands.
  // Rounding mirrors the backend per-line rule (India floors, GCC half-up).
  const roundTax = (raw: number) =>
    tax.lineRounding === 'floor' ? Math.floor(raw) : Math.floor(raw + 0.5);
  const salesTaxAmount = roundTax((Number(price) * Number(gstRate)) / 100);
  const purchaseTaxAmount = roundTax((Number(cost) * Number(purchaseGstRate)) / 100);
  const priceInclTax = Number(price) + salesTaxAmount;
  const costInclTax = Number(cost) + purchaseTaxAmount;
  // Output tax collected minus input tax credit — what actually goes to the tax office.
  const netTaxPayable = salesTaxAmount - purchaseTaxAmount;

  // Admin › Tax Configuration: the tenant's default slab / HSN are "applied
  // to new products when no specific slab is set", so a fresh form starts on
  // them (a resumed draft already carries its own values). Read from
  // /auth/me, not GET /tenants/:id, which is OWNER-only (a manager got 403
  // and stayed on 18 %). Fetched fresh so an owner's just-saved default shows.
  const tenantId = profile?.tenant_id;
  const { data: tenantTaxDefaults } = useQuery({
    queryKey: ['tenant-tax-defaults', tenantId],
    queryFn: async () => (await authService.getMe()).tenant,
    enabled: !!tenantId && !resumeDraftId,
    staleTime: 0,
  });
  const taxDefaultsSeeded = useRef(false);
  useEffect(() => {
    if (!tenantTaxDefaults || taxDefaultsSeeded.current) return;
    taxDefaultsSeeded.current = true;
    const d = tenantTaxDefaults;
    if (d.default_tax_slab != null) {
      setGstRate(String(d.default_tax_slab));
      setPurchaseGstRate(String(d.default_tax_slab));
    }
    if (d.default_hsn_code) setHsnCode(d.default_hsn_code);
  }, [tenantTaxDefaults]);

  // Active stores only: a product is never opened at a deactivated store.
  const { data: locData } = useQuery({
    queryKey: ['locations', 'active'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });

  // Tenant library of reusable variant attributes — offered as chips in the
  // Variants step. New names/values typed there are added to it on save.
  const { data: variantLibrary } = useQuery({
    queryKey: VARIANT_ATTRIBUTES_QUERY_KEY,
    queryFn: () => variantAttributeService.list(),
    staleTime: 60_000,
    enabled: !isRestaurant,
  });

  // Initialize location stock entries — a resumed draft's stock at its own store.
  useEffect(() => {
    const locs = locData?.data ?? [];
    if (resumeDraftId && !draftProduct) return;
    if (locs.length > 0 && locationStocks.length === 0) {
      setLocationStocks(locs.map((l) => ({
        location_id: l.id,
        location_name: l.name,
        quantity: draftProduct && l.id === draftProduct.location_id ? String(Number(draftProduct.stock_quantity) || 0) : '0',
      })));
    }
  }, [locData, locationStocks.length, resumeDraftId, draftProduct]);

  // Keyboard: Ctrl+Enter = submit, Esc = cancel
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === 'Escape') {
        navigate('/products');
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  /**
   * Saving an unfinished product.
   *
   * Deliberately separate from `createMutation`: a draft must not claim the
   * product was created, must not build variants (they belong to a finished
   * product), and must reuse the row it already made so repeated saves update
   * one draft instead of littering the catalogue with copies.
   */
  const draftMutation = useMutation({
    mutationFn: async (payload: CreateProductPayload) => {
      if (draftId) {
        await productService.update(draftId, toProductUpdate(payload));
        return { id: draftId, updated: true };
      }
      const created = await productService.create(payload);
      return { id: created.id, updated: false };
    },
    onSuccess: ({ id, updated }) => {
      setDraftId(id);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast({
        title: updated ? 'Draft updated' : 'Draft saved',
        description: 'Finish it any time from Products — it stays out of POS until you complete it.',
        variant: 'success',
      });
      navigate('/products');
    },
    onError: (err: Error) => {
      toast({ title: err.message || 'Could not save draft', variant: 'error' });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (payload: CreateProductPayload) => {
      // The product and all enabled variant rows go in ONE request, saved in
      // one transaction: a refused matrix saves nothing and the wizard keeps it.
      const variantRows = buildCreateVariantsPayload(variants, currency).variants;
      const withVariants = variantRows.length > 0 ? { variants: variantRows } : {};
      // Completing a resumed draft updates that row and clears the flag,
      // rather than creating a second product alongside it.
      if (draftId) {
        const { sku: _sku, location_id: _locationId, ...complete } = payload;
        return productService.completeDraft(draftId, { ...complete, ...withVariants });
      }
      return productService.create({ ...payload, ...withVariants });
    },
    onSuccess: async (created) => {
      // Gallery images need the product's id, so they go after the create,
      // in order, so the gallery keeps the order they were picked in.
      let galleryFailed = 0;
      for (const url of galleryUrls) {
        try {
          await productImageService.add(created.id, url);
        } catch {
          galleryFailed += 1;
        }
      }
      // The resumed draft is cached under the product page's own key; drop it
      // so the page fetches the completed product instead of the old draft.
      queryClient.removeQueries({ queryKey: ['product', created.id] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast({ title: 'Product created successfully', variant: 'success' });
      if (galleryFailed > 0) {
        toast({
          title: `${galleryFailed} gallery image(s) could not be added — add them on the product page`,
          variant: 'error',
        });
      }
      navigate('/products');
    },
    onError: (err: Error, payload) => {
      const msg = err.message;
      // A refusal naming a value that is not the product's own SKU / barcode
      // is about a variant row: show it on the Variants step.
      const ownValue = [payload.sku, payload.barcode].some((v) => v && msg.includes(`'${v}'`));
      if (hasVariants && !ownValue && /SKU|barcode|variant/i.test(msg)) {
        setErrors((prev) => ({ ...prev, variants: msg }));
        setCurrentStep('variants');
      } else if (/barcode/i.test(msg)) {
        // Checked before SKU: the barcode refusal names the holder's SKU.
        setErrors((prev) => ({ ...prev, barcode: msg }));
        setCurrentStep('basic');
      } else if (msg.includes('SKU')) {
        setErrors((prev) => ({ ...prev, sku: 'SKU already exists' }));
        setCurrentStep('basic');
      } else if (msg.includes('tax')) {
        setErrors((prev) => ({ ...prev, gst_rate: 'Invalid tax slab' }));
        setCurrentStep('pricing');
      } else if (/category list/i.test(msg)) {
        // C03: the category is not (or no longer) in Admin → Categories.
        setErrors((prev) => ({ ...prev, category: msg }));
        setCurrentStep('basic');
      } else {
        if (apiErrorField(err, 'hsn_code')) {
          setErrors((prev) => ({ ...prev, hsn_code: 'HSN/SAC code can be at most 8 characters' }));
          setCurrentStep('pricing');
        }
      }
    },
  });

  function validateStep(step: string): boolean {
    const errs: Record<string, string> = {};

    if (step === 'basic') {
      if (!name.trim() || name.length < 2) errs.name = 'Name must be at least 2 characters';
    }

    if (step === 'pricing') {
      if (!hasVariants) {
        if (!price || Number(price) < 0) errs.price = 'Valid selling price is required';
        if (!cost || Number(cost) < 0) errs.cost = 'Valid cost price is required';
      }
      const validRates = REGIME_PRODUCT_TAX_RATES[tax.regime];
      if (!validRates.includes(Number(gstRate))) errs.gst_rate = `Invalid ${tax.taxName} rate`;
      if (!validRates.includes(Number(purchaseGstRate))) errs.purchase_gst_rate = `Invalid ${tax.taxName} rate`;
      if (!isRestaurant && commissionPct !== '' && (Number(commissionPct) < 0 || Number(commissionPct) > 100)) {
        errs.commission_pct = 'Commission must be between 0 and 100';
      }
      if (tax.showHsn && hsnCode.trim().length > 8) {
        errs.hsn_code = 'HSN/SAC code can be at most 8 characters';
      }
    }

    // SKU is optional per variant — a blank SKU is auto-generated on submit
    // (see buildVariantPayloads), so there is no variant-completeness gate here.
    if (step === 'variants') {
      const dupes = findDuplicateCombinations(variants);
      const enabledRows = buildCreateVariantsPayload(variants, currency).variants.length;
      const missing = variantsMissingPrice(variants);
      const dupeSkus = findDuplicateIdentifiers(variants, 'sku');
      const dupeBarcodes = findDuplicateIdentifiers(variants, 'barcode');
      if (dupes.length > 0) {
        errs.variants = `Duplicate variant combination: ${dupes.join(', ')}`;
      } else if (dupeSkus.length > 0) {
        errs.variants = `Duplicate SKU ${dupeSkus.join('; ')}`;
      } else if (dupeBarcodes.length > 0) {
        errs.variants = `Duplicate barcode ${dupeBarcodes.join('; ')}`;
      } else if (hasVariants && enabledRows === 0) {
        errs.variants = 'Add at least one variant, or switch off "This product has variants" on the Pricing step';
      } else if (hasVariants && missing.length > 0) {
        errs.variants = `Set a price on every variant (missing: ${missing.join(', ')})`;
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleNext() {
    if (!validateStep(currentStep)) return;
    const idx = steps.findIndex((s) => s.id === currentStep);
    if (idx < steps.length - 1) setCurrentStep(steps[idx + 1].id);
  }

  function handleBack() {
    const idx = steps.findIndex((s) => s.id === currentStep);
    if (idx > 0) setCurrentStep(steps[idx - 1].id);
  }

  function buildPayload(): CreateProductPayload {
    // Products are location-scoped: created at the first store (a resumed draft
    // at its own), then opened at every other store, each with its own stock.
    const primaryLocationId = draftProduct?.location_id ?? locData?.data?.[0]?.id;
    const stockAt = (ls: LocationStockEntry) => Number(ls.quantity || 0);
    const primaryStock = locationStocks.find((ls) => ls.location_id === primaryLocationId);
    return {
      location_id: primaryLocationId,
      name: name.trim(),
      sku: sku.trim() || generateSku(name),
      barcode: barcode.trim() || undefined,
      category: category || undefined,
      unit,
      // Priced per variant: the parent carries no price or cost of its own.
      price: hasVariants ? 0 : Number(price),
      cost: hasVariants ? 0 : Number(cost),
      gst_rate: Number(gstRate) as GstRate,
      purchase_gst_rate: Number(purchaseGstRate) as GstRate,
      tracking: tracking === 'DEFAULT' ? undefined : tracking,
      commission_pct: !isRestaurant && commissionPct !== '' ? Number(commissionPct) : undefined,
      hsn_code: hsnCode.trim() || undefined,
      brand: brand.trim() || undefined,
      product_type: productType,
      // A parent only groups its variants; each variant carries its own stock.
      stock_quantity: hasVariants || !primaryStock ? 0 : stockAt(primaryStock),
      stocks: hasVariants
        ? undefined
        : locationStocks
          .filter((ls) => ls.location_id !== primaryLocationId)
          .map((ls) => ({ location_id: ls.location_id, quantity: stockAt(ls) })),
      // Empty = no minimum: no low-stock alerts or automatic orders (C04).
      low_stock_threshold: lowStockThreshold.trim() === '' ? null : Number(lowStockThreshold),
      is_active: isActive,
      image_url: imageUrl || undefined,
    };
  }

  function handleRemoveImage() {
    setImagePreview('');
    setImageUrl('');
    setGalleryPreviews([]);
    setGalleryUrls([]);
  }

  async function handleImageUpload(files: File[]) {
    if (!files.length) {
      handleRemoveImage();
      return;
    }
    // The first file is the main image; the rest (up to MAX_CREATE_IMAGES in
    // all) go to the gallery after the product is created.
    const picked = files.slice(0, MAX_CREATE_IMAGES);
    const previews = picked.map((f) => URL.createObjectURL(f));
    setImageUploading(true);
    try {
      const results = await Promise.allSettled(picked.map((f) => uploadProductImage(f)));
      const ok = results.flatMap((r, i) => (r.status === 'fulfilled' ? [{ key: r.value, preview: previews[i] }] : []));
      const failed = results.length - ok.length;
      results.forEach((r, i) => {
        if (r.status === 'rejected') URL.revokeObjectURL(previews[i]);
      });
      setImageUrl(ok[0]?.key ?? '');
      setImagePreview(ok[0]?.preview ?? '');
      setGalleryUrls(ok.slice(1).map((o) => o.key));
      setGalleryPreviews(ok.slice(1).map((o) => o.preview));
      if (ok.length > 0) {
        toast({ title: ok.length === 1 ? 'Image uploaded' : `${ok.length} images uploaded`, variant: 'success' });
      }
      if (failed > 0) {
        toast({ title: failed === 1 ? 'Image upload failed' : `${failed} image uploads failed`, variant: 'error' });
      }
      if (files.length > MAX_CREATE_IMAGES) {
        toast({ title: `Only the first ${MAX_CREATE_IMAGES} images were kept`, variant: 'error' });
      }
    } finally {
      setImageUploading(false);
    }
  }

  function handleSubmit() {
    for (const step of steps) {
      if (!validateStep(step.id)) {
        setCurrentStep(step.id);
        return;
      }
    }
    createMutation.mutate(buildPayload());
  }

  function handleSaveAsDraft() {
    // A draft is a partially-filled product to come back to, so it must save
    // from whatever step the user is on. Only the name is required (to find it
    // again); everything else is defaulted by applyDraftDefaults rather than
    // bouncing the user forward to a step they have not opened.
    const draftErrors = validateDraft({ name });
    if (Object.keys(draftErrors).length > 0) {
      setErrors(draftErrors);
      setCurrentStep('basic');
      return;
    }
    draftMutation.mutate(applyDraftDefaults(buildPayload() as unknown as Record<string, unknown>) as unknown as CreateProductPayload);
  }

  const currentStepIdx = steps.findIndex((s) => s.id === currentStep);
  const isLastStep = currentStepIdx === steps.length - 1;
  const progress = ((currentStepIdx + 1) / steps.length) * 100;
  const isResuming = !!resumeDraftId;

  // Resuming: don't flash an empty form before the saved values arrive, or the
  // user sees blank fields and assumes their draft was lost.
  if (isResuming && draftLoading) {
    return (
      <PageContainer title="Continue Draft" variant="compact">
        <div className="mx-auto max-w-2xl">
          <div className={cn(GLASS_FORM, 'p-6')}>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading your draft…</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={isResuming ? 'Continue Draft' : 'New Product'}
      subtitle={isResuming
        ? 'Pick up where you left off — finish the steps, then Complete Product.'
        : 'Add a new product to your catalog'}
      breadcrumbs={[
        { label: 'Products', href: '/products' },
        { label: isResuming ? 'Continue Draft' : 'New Product' },
      ]}
      variant="compact"
    >
      <div className="max-w-2xl mx-auto">
        {/* ─── Wizard Stepper ──────────────────────────────────────── */}
        <div className={cn(GLASS, 'p-4 mb-6')}>
          {/* Progress bar */}
          <div className="h-1 rounded-full bg-neutral-100 dark:bg-neutral-800 mb-4 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Step ${currentStepIdx + 1} of ${steps.length}`}
            />
          </div>

          {/* Step indicators */}
          <nav className="flex items-center gap-1 overflow-x-auto" aria-label="Form steps">
            {steps.map((step, idx) => {
              const isCompleted = idx < currentStepIdx;
              const isCurrent = step.id === currentStep;

              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => {
                    // Only allow navigating to completed steps or current
                    if (idx <= currentStepIdx) setCurrentStep(step.id);
                  }}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap',
                    isCurrent && 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
                    isCompleted && 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20',
                    !isCurrent && !isCompleted && 'text-neutral-400 dark:text-neutral-500 cursor-default',
                  )}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  <span className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold shrink-0',
                    isCurrent && 'bg-teal-600 text-white',
                    isCompleted && 'bg-emerald-500 text-white',
                    !isCurrent && !isCompleted && 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 dark:text-neutral-500',
                  )}>
                    {isCompleted ? (
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      idx + 1
                    )}
                  </span>
                  <span className="hidden sm:inline">{step.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* ─── Form Card (Glass) ──────────────────────────────────── */}
        <div className={cn(GLASS_FORM, 'p-6')}>
          {/* ─── Step: Basic Info ─── */}
          {currentStep === 'basic' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', toneClasses('teal').icon)}>
                  <StepIcon d={steps[0].icon} />
                </div>
                <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                  Basic Information
                </h3>
              </div>

              <FormField label="Product Name" htmlFor="new-name" required error={errors.name}>
                <Input id="new-name" placeholder="e.g., Basmati Rice 5kg" value={name} onChange={setName} />
              </FormField>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="SKU" htmlFor="new-sku" error={errors.sku} helperText="Optional — auto-generated if left blank">
                  <Input id="new-sku" placeholder="e.g., RICE-BAS-5KG" value={sku} onChange={setSku} />
                </FormField>

                <FormField label="Barcode" htmlFor="new-barcode" error={errors.barcode}>
                  <BarcodeScanField
                    id="new-barcode"
                    value={barcode}
                    onChange={setBarcode}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Category" htmlFor="new-category" error={errors.category}>
                  {/* Pick an existing category, or type a new name and create it
                      from the dropdown — it is added to the catalog immediately. */}
                  <CategoryCombobox
                    id="new-category"
                    value={category}
                    onChange={setCategory}
                    placeholder="Select or type a category (e.g. Breakfast)"
                  />
                </FormField>

                <FormField label="Unit of Measure" htmlFor="new-unit">
                  <Select id="new-unit" options={UNIT_OPTIONS as unknown as { value: string; label: string }[]} value={unit} onChange={(v) => setUnit(typeof v === 'string' ? v : 'pcs')} />
                </FormField>
              </div>

              <FormField label="Description" htmlFor="new-desc">
                <TextArea id="new-desc" placeholder="Product description (optional)" value={description} onChange={setDescription} rows={3} />
              </FormField>

              <div className="pt-2">
                <Toggle checked={isActive} onChange={setIsActive} label="Active (visible on POS)" />
              </div>
            </div>
          )}

          {/* ─── Step: Pricing & Tax ─── */}
          {currentStep === 'pricing' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', toneClasses('brand').icon)}>
                  <StepIcon d={steps[1].icon} />
                </div>
                <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                  Pricing & Tax
                </h3>
              </div>

              {!isRestaurant && (
                <div className="rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-indigo-50 p-4 dark:border-violet-800 dark:from-violet-900/20 dark:to-indigo-900/20">
                  <Toggle
                    id="new-has-variants"
                    checked={hasVariants}
                    onChange={setHasVariants}
                    label="This product has variants (sizes, colours…)"
                  />
                  <p className="mt-1 text-xs text-violet-700 dark:text-violet-300">
                    {hasVariants
                      ? 'Each variant gets its own selling price and cost on the Variants step.'
                      : 'Switch on to price each size or colour separately instead of one price for the product.'}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!hasVariants && (
                <>
                <FormField label={`Selling Price (${getCurrencySymbol(currency)})`} htmlFor="new-price" required error={errors.price}>
                  <Input
                    id="new-price"
                    type="number"
                    variant="currency"
                    currency={currency}
                    placeholder="e.g., 399"
                    value={priceRupees}
                    onChange={(v) => {
                      setPriceRupees(v);
                      setPrice(String(rupeeInputToPaise(v, currency) ?? ''));
                    }}
                    min={0}
                  />
                  {price && Number(price) > 0 && (
                    <p className="mt-1 text-xs text-neutral-400 tabular-nums">
                      Display: {formatPaise(Number(price), currency)}
                    </p>
                  )}
                </FormField>

                <FormField label={`Cost Price (${getCurrencySymbol(currency)})`} htmlFor="new-cost" required error={errors.cost}>
                  <Input
                    id="new-cost"
                    type="number"
                    variant="currency"
                    currency={currency}
                    placeholder="e.g., 140"
                    value={costRupees}
                    onChange={(v) => {
                      setCostRupees(v);
                      setCost(String(rupeeInputToPaise(v, currency) ?? ''));
                    }}
                    min={0}
                  />
                  {cost && Number(cost) > 0 && (
                    <p className="mt-1 text-xs text-neutral-400 tabular-nums">
                      Display: {formatPaise(Number(cost), currency)}
                    </p>
                  )}
                </FormField>
                </>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label={`Sales ${tax.taxName} (on selling price)`} htmlFor="new-gst" required error={errors.gst_rate}>
                  <Select
                    id="new-gst"
                    options={buildTaxSlabOptions(tax.taxName, tax.regime)}
                    value={gstRate}
                    onChange={(v) => setGstRate(typeof v === 'string' ? v : '18')}
                  />
                </FormField>

                <FormField label={`Purchase ${tax.taxName} (on cost price)`} htmlFor="new-purchase-gst" required error={errors.purchase_gst_rate}>
                  <Select
                    id="new-purchase-gst"
                    options={buildTaxSlabOptions(tax.taxName, tax.regime)}
                    value={purchaseGstRate}
                    onChange={(v) => setPurchaseGstRate(typeof v === 'string' ? v : '18')}
                  />
                </FormField>

                <FormField label="Tracking on receipt" htmlFor="new-tracking" helperText="What Receive Goods asks for. Untracked unless enabled here or tenant-wide in Settings → Inventory Tracking.">
                  <Select
                    id="new-tracking"
                    options={TRACKING_OPTIONS}
                    value={tracking}
                    onChange={(v) => setTracking((typeof v === 'string' ? v : 'DEFAULT') as TrackingOverride)}
                  />
                </FormField>

                {/* Per-item commission — retail only (Flat / Item commission rates). */}
                {!isRestaurant && (
                  <FormField label="Commission (%)" htmlFor="new-commission" error={errors.commission_pct}>
                    <Input
                      id="new-commission"
                      type="number"
                      min={0}
                      max={100}
                      placeholder="e.g. 5"
                      value={commissionPct}
                      onChange={setCommissionPct}
                    />
                  </FormField>
                )}

                <FormField label="Brand" htmlFor="new-brand">
                  <Input
                    id="new-brand"
                    placeholder="e.g., boAt"
                    value={brand}
                    onChange={setBrand}
                  />
                </FormField>

                <FormField
                  label="Product Type"
                  htmlFor="new-product-type"
                  helperText="Services are not stock-tracked."
                >
                  <Select
                    options={PRODUCT_TYPE_OPTIONS}
                    value={productType}
                    onChange={(v) => setProductType(typeof v === 'string' ? v : 'GOODS')}
                  />
                </FormField>

                {tax.showHsn && (
                  <FormField label="HSN/SAC Code" htmlFor="new-hsn" error={errors.hsn_code}>
                    <Tooltip content="Harmonized System of Nomenclature code for GST classification">
                      <Input id="new-hsn" placeholder="e.g., 1006" value={hsnCode} onChange={setHsnCode} maxLength={8} />
                    </Tooltip>
                  </FormField>
                )}
              </div>

              {/* Margin indicator */}
              {!hasVariants && price && cost && Number(price) > 0 && Number(cost) > 0 && (
                <div className={cn(
                  'rounded-xl p-4 flex items-center gap-3',
                  Number(price) > Number(cost)
                    ? 'bg-emerald-50/80 dark:bg-emerald-900/20'
                    : 'bg-rose-50/80 dark:bg-rose-900/20',
                )}>
                  <div className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-lg',
                    Number(price) > Number(cost)
                      ? 'bg-emerald-100 dark:bg-emerald-800/40 text-emerald-600 dark:text-emerald-400'
                      : 'bg-rose-100 dark:bg-rose-800/40 text-rose-600 dark:text-rose-400',
                  )}>
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
                      Margin: {formatPaise(Number(price) - Number(cost), currency)}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                      {marginPercent(Number(price), Number(cost))}% margin
                      <span className="ml-1">(excl. {tax.taxName})</span>
                    </p>

                    {/* {tax.taxName} is a pass-through tax — collected on the sale
                        and reclaimed on the purchase — so it is deliberately kept
                        out of the margin above. These lines show the actual cash
                        the customer pays and the supplier charges. */}
                    <div className="mt-3 space-y-1 border-t border-neutral-200/70 dark:border-neutral-700/70 pt-2">
                      <div className="flex justify-between gap-4 text-xs">
                        <span className="text-neutral-500 dark:text-neutral-400">
                          Customer pays (incl. {gstRate}% {tax.taxName})
                        </span>
                        <span className="font-medium text-neutral-700 dark:text-neutral-200 tabular-nums">
                          {formatPaise(priceInclTax, currency)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4 text-xs">
                        <span className="text-neutral-500 dark:text-neutral-400">
                          You pay supplier (incl. {purchaseGstRate}% {tax.taxName})
                        </span>
                        <span className="font-medium text-neutral-700 dark:text-neutral-200 tabular-nums">
                          {formatPaise(costInclTax, currency)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4 text-xs">
                        <span className="text-neutral-500 dark:text-neutral-400">
                          Net {tax.taxName} payable
                        </span>
                        <span className="font-medium text-neutral-700 dark:text-neutral-200 tabular-nums">
                          {formatPaise(netTaxPayable, currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── Step: Stock ─── */}
          {currentStep === 'stock' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', toneClasses('teal').icon)}>
                  <StepIcon d={steps[2].icon} />
                </div>
                <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                  Opening Stock
                </h3>
              </div>

              <FormField label="Min Stock (low-stock alert)" htmlFor="new-threshold">
                <Input id="new-threshold" type="number" placeholder="No minimum" value={lowStockThreshold} onChange={setLowStockThreshold} min={0} />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  Leave empty for no low-stock alerts or automatic orders. 0 = alert only when out of stock.
                </p>
              </FormField>

              <div className="space-y-3 mt-4">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Stock per Location
                </label>
                {hasVariants ? (
                  <div className="rounded-xl bg-neutral-50/50 dark:bg-neutral-800/30 p-4 text-center">
                    <p className="text-sm text-neutral-500">
                      This product has variants, so enter the stock for each variant in the Variants step.
                    </p>
                  </div>
                ) : locationStocks.length === 0 ? (
                  <div className="rounded-xl bg-neutral-50/50 dark:bg-neutral-800/30 p-4 text-center">
                    <p className="text-sm text-neutral-500">No locations found. Add locations first.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {locationStocks.map((ls, idx) => (
                      <div key={ls.location_id} className="flex items-center gap-3 rounded-lg bg-white/50 dark:bg-neutral-800/30 p-3">
                        <span className="text-sm text-neutral-700 dark:text-neutral-300 w-40 truncate font-medium">
                          {ls.location_name}
                        </span>
                        <Input
                          type="number"
                          value={ls.quantity}
                          onChange={(v) => {
                            const updated = [...locationStocks];
                            updated[idx] = { ...updated[idx], quantity: typeof v === 'string' ? v : String(v) };
                            setLocationStocks(updated);
                          }}
                          min={0}
                          className="w-32"
                          aria-label={`Stock for ${ls.location_name}`}
                        />
                        <span className="text-xs text-neutral-400">{unit}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Step: Images ─── */}
          {currentStep === 'images' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', toneClasses('violet').icon)}>
                  <StepIcon d={steps[3].icon} />
                </div>
                <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                  Product Images
                </h3>
              </div>

              <FileUpload
                accept="image/*"
                maxSize={MAX_RAW_UPLOAD_BYTES}
                multiple
                variant="dropzone"
                onUpload={handleImageUpload}
              />
              {imageUploading && (
                <p className="text-xs text-neutral-400">Uploading image…</p>
              )}
              {imagePreview && (
                <div className="flex flex-wrap items-center gap-4">
                  <img
                    src={imagePreview}
                    alt="Product image preview"
                    className="h-24 w-24 rounded-xl object-cover border border-neutral-200 dark:border-neutral-700 shadow-md"
                  />
                  {galleryPreviews.map((src, i) => (
                    <img
                      key={src}
                      src={src}
                      alt={`Gallery image ${i + 1} preview`}
                      className="h-20 w-20 rounded-xl object-cover border border-neutral-200 dark:border-neutral-700 shadow-sm"
                    />
                  ))}
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="text-xs font-medium text-rose-600 hover:text-rose-700 dark:text-rose-400"
                  >
                    Remove
                  </button>
                </div>
              )}
              {imageUrl && !imageUploading && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400">{galleryUrls.length > 0
                    ? `${galleryUrls.length + 1} images uploaded: the first is the main image, the rest go to the product's gallery.`
                    : 'Image uploaded and will be attached to the product.'}</p>
              )}
              <p className="text-xs text-neutral-400">Max 5 images, up to 10MB each (compressed on upload). Accepted: JPG, PNG, WebP</p>
            </div>
          )}

          {/* ─── Step: Variants (RETAIL) ─── */}
          {currentStep === 'variants' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', toneClasses('violet').icon)}>
                  <StepIcon d={RETAIL_EXTRA.icon} />
                </div>
                <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                  Variant Matrix
                </h3>
              </div>

              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Pick attributes from your library or add new ones, choose each attribute's values,
                then generate every combination. Each variant gets its own SKU, barcode, price, cost
                and stock.{' '}
                {hasVariants
                  ? 'This product is priced per variant, so give every variant a price.'
                  : 'Variants are optional — leave this empty to create a single product.'}
              </p>

              <VariantMatrixBuilder
                attributes={attributes}
                onAttributesChange={setAttributes}
                variants={variants}
                onVariantsChange={setVariants}
                library={variantLibrary ?? []}
                parentSku={sku.trim() || generateSku(name)}
                parentPrice={Number(price) || 0}
                parentCost={Number(cost) || 0}
                currency={currency}
                error={errors.variants}
              />
            </div>
          )}

          {/* ─── Step: Recipe (RESTAURANT) ─── */}
          {currentStep === 'recipe' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', toneClasses('warning').icon)}>
                  <StepIcon d={RESTAURANT_EXTRA.icon} />
                </div>
                <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                  Recipe / BOM
                </h3>
              </div>

              <div className="rounded-xl bg-gradient-to-br from-amber-50/60 to-orange-50/40 dark:from-amber-900/15 dark:to-orange-900/10 p-6 text-center">
                <div className="flex h-14 w-14 mx-auto items-center justify-center rounded-2xl bg-amber-100 dark:bg-amber-900/30 text-amber-500 dark:text-amber-400 mb-4">
                  <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                  Recipe & Ingredient Breakdown
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 max-w-xs mx-auto">
                  Recipe linking with Bill of Materials will be available after Recipe module integration. Stock deduction will follow recipe/BOM.
                </p>
              </div>
            </div>
          )}

          {/* ─── Navigation ─── */}
          <div className="flex items-center justify-between mt-8 pt-6">
            <div className="flex gap-2">
              {currentStepIdx > 0 && (
                <Button variant="ghost" onClick={handleBack}>Back</Button>
              )}
              <Button variant="ghost" onClick={() => navigate('/products')}>Cancel</Button>
            </div>

            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={handleSaveAsDraft}
                disabled={createMutation.isPending || draftMutation.isPending}
              >
                {draftMutation.isPending ? 'Saving…' : 'Save as Draft'}
              </Button>
              {isLastStep ? (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={createMutation.isPending || draftMutation.isPending}
                >
                  {createMutation.isPending
                    ? (draftId ? 'Completing…' : 'Creating…')
                    : (draftId ? 'Complete Product' : 'Create Product')}
                </Button>
              ) : (
                <Button variant="primary" onClick={handleNext}>
                  Next
                </Button>
              )}
            </div>
          </div>
        </div>

        {createMutation.isError && (
          <div className="mt-4">
            <Alert
              variant="danger"
              title={apiErrorMessage(createMutation.error, 'Failed to create product')}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export function ProductCreatePage() {
  return (
    <ErrorBoundary>
      <ProductCreateContent />
    </ErrorBoundary>
  );
}
